<template>
  <div
    class="org-select-page w-full min-h-[100dvh] relative flex flex-col"
    style="background-image: url('data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1h2v2H1V1zm4 0h2v2H5V1zm4 0h2v2H9V1zm4 0h2v2h-2V1zm4 0h2v2h-2V1zm-16 4h2v2H1V5zm4 0h2v2H5V5zm4 0h2v2H9V5zm4 0h2v2h-2V5zm4 0h2v2h-2V5zm-16 4h2v2H1V9zm4 0h2v2H5V9zm4 0h2v2H9V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9zm-16 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z\' fill=\'%239C92AC\' fill-opacity=\'0.05\' fill-rule=\'evenodd\'/%3E%3C/svg%3E');"
  >
    <div class="org-select-center my-auto w-full max-w-3xl xl:max-w-5xl 2xl:max-w-6xl mx-auto px-4 pt-[max(1.75rem,calc(env(safe-area-inset-top)+0.75rem))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-10 xl:px-8 xl:py-14 2xl:py-16">
      <!-- Header -->
      <header class="relative z-10">
        <div class="flex flex-col items-center text-center">
          <div class="relative mb-3 sm:mb-4 xl:mb-5">
            <div class="absolute -inset-1 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 opacity-70 blur-[2px]"></div>
            <img
              class="relative h-14 w-14 sm:h-[4.25rem] sm:w-[4.25rem] xl:h-20 xl:w-20 2xl:h-[5.5rem] 2xl:w-[5.5rem] rounded-full object-cover ring-[3px] ring-white shadow-lg"
              :src="mainStore.user?.profileImage"
              alt="User profile"
            />
          </div>

          <p class="text-[11px] sm:text-xs xl:text-sm font-semibold uppercase tracking-[0.18em] text-teal-700/80 mb-1">
            RefReps
          </p>
          <h1 class="text-[1.35rem] sm:text-3xl xl:text-[2.25rem] 2xl:text-4xl font-bold text-slate-800 tracking-tight leading-tight px-2">
            Welcome back{{ firstName ? ',' : '' }}
            <span v-if="firstName" class="text-teal-700">{{ firstName }}</span>
          </h1>
          <p class="text-sm sm:text-[0.95rem] xl:text-lg text-slate-500 mt-1.5 xl:mt-2 px-4 max-w-md xl:max-w-xl leading-snug">
            Choose an organization to continue managing your learners and courses
          </p>

          <div
            v-if="orgCount"
            class="mt-3 xl:mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-teal-100 px-3 py-1 xl:px-3.5 xl:py-1.5 text-xs xl:text-sm font-medium text-slate-600 shadow-sm backdrop-blur-sm"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-teal-500"></span>
            {{ orgCount }} organization{{ orgCount === 1 ? '' : 's' }} available
          </div>
        </div>
      </header>

      <!-- Organizations table -->
      <div class="relative z-10 mt-5 sm:mt-6 xl:mt-8 2xl:mt-10">
        <div class="org-table-shell bg-white/90 backdrop-blur-sm rounded-2xl xl:rounded-3xl border border-white shadow-[0_8px_30px_rgba(15,118,110,0.08)] overflow-hidden ring-1 ring-slate-200/70">
          <!-- Desktop / tablet table -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="w-full border-collapse text-left">
              <thead>
                <tr class="border-b border-slate-100 bg-gradient-to-r from-teal-50/90 via-white to-slate-50/80">
                  <th class="px-5 py-3.5 xl:px-6 xl:py-4 2xl:px-7 2xl:py-5 text-[11px] xl:text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Organization
                  </th>
                  <th
                    v-for="col in statColumns"
                    :key="col.key"
                    class="px-3 py-3.5 xl:px-4 xl:py-4 2xl:py-5 text-[11px] xl:text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 text-center whitespace-nowrap"
                  >
                    {{ col.label }}
                  </th>
                  <th class="px-4 py-3.5 xl:px-5 xl:py-4 2xl:py-5 w-12 xl:w-14">
                    <span class="sr-only">Select</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(organization, index) in mainStore.organizations"
                  :key="organization.id"
                  @click="selectOrganization(organization)"
                  class="org-row group border-b border-slate-100/80 last:border-b-0 cursor-pointer transition-all duration-200"
                  :class="{ 'pointer-events-none opacity-60': isLoadingInstructor && selectedOrgId === organization.companyID }"
                  :style="{ animationDelay: `${index * 40}ms` }"
                >
                  <td class="px-5 py-3.5 xl:px-6 xl:py-5 2xl:px-7 2xl:py-6 relative">
                    <div class="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <div class="flex items-center gap-3.5 xl:gap-4 min-w-0">
                      <img
                        :src="organization.profileImage ?? 'https://host.refreps.com/images/org_default.webp'"
                        alt=""
                        class="w-11 h-11 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 rounded-xl object-cover border border-slate-200/80 flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow"
                      />
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="font-semibold text-slate-800 truncate group-hover:text-teal-800 transition-colors xl:text-lg">
                            {{ organization.name }}
                          </span>
                          <span
                            v-if="organization.notificationCount > 0"
                            class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex-shrink-0 shadow-sm"
                          >
                            {{ organization.notificationCount }}
                          </span>
                        </div>
                        <p class="text-xs xl:text-sm text-slate-400 mt-0.5">Instructor access</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-3.5 xl:px-4 xl:py-5 2xl:py-6 text-center">
                    <span class="stat-value">{{ organization.numberOfUsers ?? '—' }}</span>
                  </td>
                  <td class="px-3 py-3.5 xl:px-4 xl:py-5 2xl:py-6 text-center">
                    <span class="stat-value">{{ organization.activeUsers ?? '—' }}</span>
                  </td>
                  <td class="px-3 py-3.5 xl:px-4 xl:py-5 2xl:py-6 text-center">
                    <span class="stat-value">{{ organization.prepCourses != null ? organization.prepCourses.length : '—' }}</span>
                  </td>
                  <td class="px-3 py-3.5 xl:px-4 xl:py-5 2xl:py-6 text-center">
                    <span class="stat-value">{{ organization.seats ?? '—' }}</span>
                  </td>
                  <td class="px-4 py-3.5 xl:px-5 xl:py-5 2xl:py-6 text-center">
                    <div class="flex items-center justify-center w-8 h-8 xl:w-9 xl:h-9 mx-auto rounded-full bg-slate-50 group-hover:bg-teal-50 transition-colors">
                      <font-awesome-icon
                        v-if="isLoadingInstructor && selectedOrgId === organization.companyID"
                        icon="fa-duotone fa-solid fa-spinner-third"
                        spin
                        class="w-3.5 h-3.5 xl:w-4 xl:h-4 text-teal-500"
                      />
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3.5 w-3.5 xl:h-4 xl:w-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all duration-200"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2.25"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile rows -->
          <div class="sm:hidden divide-y divide-slate-100/90">
            <button
              v-for="(organization, index) in mainStore.organizations"
              :key="organization.id"
              type="button"
              @click="selectOrganization(organization)"
              class="org-row mobile-row w-full text-left px-3.5 py-3 transition-colors duration-150 active:bg-teal-50/80 disabled:opacity-60"
              :disabled="isLoadingInstructor && selectedOrgId === organization.companyID"
              :style="{ animationDelay: `${index * 40}ms` }"
            >
              <div class="flex items-center gap-3 mb-2.5">
                <img
                  :src="organization.profileImage ?? 'https://host.refreps.com/images/org_default.webp'"
                  alt=""
                  class="w-10 h-10 rounded-xl object-cover border border-slate-200 flex-shrink-0 shadow-sm"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-slate-800 text-[0.925rem] truncate">{{ organization.name }}</span>
                    <span
                      v-if="organization.notificationCount > 0"
                      class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex-shrink-0"
                    >
                      {{ organization.notificationCount }}
                    </span>
                  </div>
                  <p class="text-[11px] text-slate-400 mt-0.5">Instructor access</p>
                </div>
                <div class="flex items-center justify-center w-7 h-7 rounded-full bg-slate-50 flex-shrink-0">
                  <font-awesome-icon
                    v-if="isLoadingInstructor && selectedOrgId === organization.companyID"
                    icon="fa-duotone fa-solid fa-spinner-third"
                    spin
                    class="w-3.5 h-3.5 text-teal-500"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5 text-slate-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.25"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <div class="grid grid-cols-4 gap-1.5">
                <div
                  v-for="col in statColumns"
                  :key="col.key"
                  class="rounded-xl bg-gradient-to-b from-slate-50 to-teal-50/40 border border-slate-100/80 px-1 py-1.5 text-center"
                >
                  <div class="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-semibold">{{ col.short }}</div>
                  <div class="text-[0.8125rem] font-bold text-slate-800 tabular-nums mt-0.5 leading-none">
                    {{ getStat(organization, col.key) }}
                  </div>
                </div>
              </div>
            </button>
          </div>

          <div
            v-if="!mainStore.organizations?.length"
            class="px-5 py-12 text-center"
          >
            <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-300">
              <font-awesome-icon icon="fa-regular fa-solid fa-users" class="w-5 h-5" />
            </div>
            <p class="text-sm font-medium text-slate-600">No organizations available</p>
            <p class="text-xs text-slate-400 mt-1">Contact support if this looks wrong</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import useMainStore from '@/stores/main'

export default {
  computed: {
    ...mapStores(useMainStore),
    firstName() {
      const name = this.mainStore.user?.name
      if (!name) return ''
      return String(name).trim().split(/\s+/)[0]
    },
    orgCount() {
      return this.mainStore.organizations?.length || 0
    },
    statColumns() {
      return [
        { key: 'users', label: 'Users', short: 'Users' },
        { key: 'active', label: 'Active', short: 'Active' },
        { key: 'courses', label: 'Courses', short: 'Courses' },
        { key: 'seats', label: 'Seats', short: 'Seats' },
      ]
    },
  },
  data() {
    return {
      isLoadingInstructor: false,
      selectedOrgId: null,
    }
  },
  methods: {
    getStat(organization, key) {
      if (key === 'users') return organization.numberOfUsers ?? '—'
      if (key === 'active') return organization.activeUsers ?? '—'
      if (key === 'courses') return organization.prepCourses != null ? organization.prepCourses.length : '—'
      if (key === 'seats') return organization.seats ?? '—'
      return '—'
    },
    selectOrganization(organization) {
      this.isLoadingInstructor = true
      this.selectedOrgId = organization.companyID
      this.mainStore.organization = organization
      this.mainStore.showLoginType = 'login'
      setTimeout(() => {
        this.$router.push({
          name: 'organization',
          params: {
            id: organization.companyID
          }
        })
      }, 1000)
    },
  },
}
</script>

<style scoped>
.org-row {
  animation: org-row-in 0.35s ease-out both;
}

.org-row:hover {
  background: linear-gradient(90deg, rgba(240, 253, 250, 0.95), rgba(255, 255, 255, 0.4));
}

.stat-value {
  display: inline-flex;
  min-width: 2rem;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  transition: all 0.2s ease;
}

@media (min-width: 1280px) {
  .stat-value {
    min-width: 2.5rem;
    padding: 0.3rem 0.7rem;
    font-size: 0.9375rem;
  }
}

.org-row:hover .stat-value {
  background: #f0fdfa;
  border-color: #99f6e4;
  color: #0f766e;
}

@keyframes org-row-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .org-row {
    animation: none;
  }
}
</style>
